import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Attach24Regular,
  Checkmark20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  ContactCard24Regular,
  Dismiss20Regular,
  DocumentText20Regular,
  Edit20Regular,
  Location24Regular,
  Mic24Regular,
  Send24Regular,
  Tag24Regular,
  Warning24Regular,
  WeatherSnow24Regular,
} from '@fluentui/react-icons';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { useAppStore } from '@/stores/chatbotStore';
import { Message, SourceDocument } from '@/types/api';
import DocumentManager from '@/components/DocumentManager';
import MermaidDiagram from '@/components/MermaidDiagram';
import { MapBlock } from '@/components/PlacesMap';
import ConfirmationCard from '@/components/ConfirmationCard';
import ReservationCard from '@/components/ReservationCard';
import VoiceWaveform from '@/components/VoiceWaveform';
import apiClient from '@/services/api';
import { MicrophoneUnavailableError, VoiceRecorder, canRecord } from '@/utils/audio';

/* ------------------------------------------------------------------ */
/*  SourceCard                                                        */
/* ------------------------------------------------------------------ */

const SourceCard: React.FC<{ source: SourceDocument; index: number }> = ({ source, index }) => {
  const [expanded, setExpanded] = useState(false);

  const scoreColor = () => {
    if (source.score == null) return null;
    if (source.score >= 0.7) return { bg: '#dff6dd', fg: '#107c10' };
    if (source.score >= 0.4) return { bg: '#fff4ce', fg: '#797600' };
    return { bg: '#fed9cc', fg: '#d83b01' };
  };

  const sc = scoreColor();

  return (
    <div className="source-card" style={{ flexDirection: 'column', alignItems: 'stretch', padding: '10px 12px', cursor: 'default' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="source-number">{index + 1}</span>
          <strong style={{ fontSize: 12 }}>
            {source.url ? (
              <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                {source.filename}
              </a>
            ) : source.filename}
          </strong>
          {source.page != null && (
            <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12 }}> - p.{source.page}</span>
          )}
        </span>
        {sc && (
          <span style={{ background: sc.bg, color: sc.fg, padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
            {(source.score! * 100).toFixed(0)}%
          </span>
        )}
      </div>

      <p style={{
        color: '#6b7280',
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
        margin: '6px 0 0',
        ...(!expanded && {
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical' as never,
          overflow: 'hidden',
        }),
      }}>
        {source.text_excerpt}
      </p>

      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-primary)', fontSize: 11, fontWeight: 600,
          padding: '4px 0 0', textAlign: 'left',
        }}
      >
        {expanded ? 'Voir moins' : 'Voir plus'}
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  SourcesBubble                                                     */
/* ------------------------------------------------------------------ */

const SourcesBubble: React.FC<{ sources: SourceDocument[] }> = ({ sources }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="sources-bubble">
      <button
        className="sources-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '10px 14px', background: 'none', border: 'none',
          cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#6b7280',
          width: '100%', textAlign: 'left',
        }}
      >
        {isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        <DocumentText20Regular />
        {sources.length} source{sources.length > 1 ? 's' : ''} utilisee{sources.length > 1 ? 's' : ''}
      </button>

      {isExpanded && (
        <div className="sources-list" style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sources.map((source, index) => (
            <SourceCard key={source.node_id || index} source={source} index={index} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  MessageItem                                                       */
/* ------------------------------------------------------------------ */

interface MessageItemProps {
  message: Message;
  isLatest?: boolean;
  isStreaming?: boolean;
}

/** "https://skipass.alpedhuez.com/hiver/court-sejour/" -> "skipass.alpedhuez.com › court-sejour" */
const prettyUrl = (href: string) => {
  try {
    const u = new URL(href);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last ? `${u.hostname} › ${decodeURIComponent(last).replace(/[-_]/g, ' ')}` : u.hostname;
  } catch {
    return href;
  }
};

/* ------------------------------------------------------------------ */
/*  Media (images, PDF) inside an answer                              */
/* ------------------------------------------------------------------ */

/** Used until /api/info answers; the server list is authoritative. */
const DEFAULT_MEDIA_HOSTS = ['alpedhuez.com', 'aten-altitude.com'];

/**
 * Raw HTML goes through rehype-raw, so an answer could carry any URL. Only the
 * official hosts are ever loaded — over https, so a media never downgrades the page.
 */
const isAllowedMedia = (url: string | undefined, hosts: string[]): boolean => {
  if (!url) return false;
  try {
    const u = new URL(url, window.location.origin);
    if (u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    return hosts.some((d) => host === d || host.endsWith('.' + d));
  } catch {
    return false;
  }
};

const isPdf = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    return new URL(url, window.location.origin).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
};

const ChatImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer">
        {alt || "Voir l'image"}
      </a>
    );
  }
  return (
    <img
      className="chat-image"
      src={src}
      alt={alt}
      title={alt || undefined}
      loading="lazy"
      onError={() => setFailed(true)}
      onClick={() => window.open(src, '_blank', 'noopener')}
    />
  );
};

/**
 * `autoOpen`: the document was just asked for, so it is shown right away. Older
 * answers stay folded — reopening a conversation must not reload every PDF in it.
 */
const PdfCard: React.FC<{ href: string; label: string; autoOpen?: boolean }> = ({ href, label, autoOpen = false }) => {
  const [open, setOpen] = useState(false);
  const opened = useRef(false);

  // autoOpen turns true once the answer has finished streaming. Open once and keep
  // it open: the card must not fold back as soon as the next question is sent.
  useEffect(() => {
    if (autoOpen && !opened.current) {
      opened.current = true;
      setOpen(true);
    }
  }, [autoOpen]);
  // skipass.alpedhuez.com sets frame-ancestors 'self', so its PDFs can only be
  // framed once relayed by our own origin.
  const framed = `/api/media/pdf?url=${encodeURIComponent(href)}`;

  return (
    <span className="chat-pdf">
      <span className="chat-pdf-head">
        <DocumentText20Regular />
        <span className="chat-pdf-title">{label}</span>
        <button type="button" className="chat-pdf-toggle" onClick={() => setOpen(!open)}>
          {open ? 'Masquer' : 'Afficher'}
        </button>
        <a href={href} target="_blank" rel="noopener noreferrer" className="chat-pdf-open">
          Ouvrir
        </a>
      </span>
      {open && <iframe className="chat-pdf-frame" src={framed} title={label} />}
    </span>
  );
};

const MessageItem: React.FC<MessageItemProps> = ({ message, isLatest, isStreaming }) => {
  const isUser = message.role === 'user';
  const mediaHosts = useAppStore((s) => s.appInfo?.media_domains) ?? DEFAULT_MEDIA_HOSTS;
  // A document is only ever returned on request: show it unfolded on the answer
  // that was just given, and leave the ones further up the history folded
  const showMedia = isLatest && !isStreaming;

  return (
    <div className={`message ${isUser ? 'message-user' : 'message-assistant'}`}>
      {!isUser && <div className="message-avatar" aria-hidden="true"><WeatherSnow24Regular /></div>}
      <div className="message-body">
      <div className="message-content">
        {isUser ? (
          <p>{message.content}</p>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              // Tables scroll inside the bubble instead of overflowing it
              table({ children }: any) {
                return (
                  <div className="table-wrap">
                    <table>{children}</table>
                  </div>
                );
              },
              // An image of the official sites is shown inline; anything else is dropped
              img({ src, alt }: any) {
                const label = String(alt ?? '');
                if (!isAllowedMedia(src, mediaHosts)) return label ? <em>{label}</em> : null;
                // The model sometimes writes a PDF with image syntax: an <img> cannot
                // show it, so it would silently degrade to a bare link
                if (isPdf(src)) {
                  return <PdfCard href={String(src)} label={label || prettyUrl(String(src))} autoOpen={showMedia} />;
                }
                return <ChatImage src={String(src)} alt={label} />;
              },
              // A bare URL is shown as a short, readable label; every link opens in a new tab
              a({ href, children }: any) {
                const text = Array.isArray(children) ? children.join('') : String(children ?? '');
                // A PDF of the official sites gets a viewer instead of a download link
                if (isPdf(href) && isAllowedMedia(href, mediaHosts)) {
                  return <PdfCard href={href} label={text || prettyUrl(href)} autoOpen={showMedia} />;
                }
                const bare = href && (text === href || text === href.replace(/\/$/, ''));
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" className={bare ? 'link-url' : undefined}>
                    {bare ? prettyUrl(href) : children}
                  </a>
                );
              },
              // Every fenced block comes wrapped in a dark <pre>: a diagram must not
              // sit inside it, or it gets a navy frame above and below
              pre({ node, children }: any) {
                const lang = node?.children?.[0]?.properties?.className;
                if (Array.isArray(lang) && (lang.includes('language-mermaid') || lang.includes('language-map'))) {
                  return <>{children}</>;
                }
                return <pre>{children}</pre>;
              },
              code({ node, inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || '');
                if (!inline && match?.[1] === 'map') {
                  return <MapBlock code={String(children).replace(/\n$/, '')} isStreaming={isStreaming} />;
                }
                if (!inline && match?.[1] === 'mermaid') {
                  return (
                    <MermaidDiagram
                      code={String(children).replace(/\n$/, '')}
                      isStreaming={isStreaming}
                    />
                  );
                }
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={oneLight}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>

      {/* Ski pass reservation card */}
      {!isUser && message.reservation && !isStreaming && (
        <ReservationCard reservation={message.reservation} />
      )}

      {/* Source attribution for assistant messages */}
      {!isUser && message.source_documents && message.source_documents.length > 0 && !isStreaming && (
        <SourcesBubble sources={message.source_documents} />
      )}

      {/* Metadata row */}
      {message.response_time && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
          {message.response_time.toFixed(1)}s
        </div>
      )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  ThinkingIndicator                                                 */
/* ------------------------------------------------------------------ */

const Dots: React.FC = () => (
  <div className="dots" aria-hidden="true">
    <span className="dot" />
    <span className="dot" />
    <span className="dot" />
  </div>
);

const FIRST_STEP = 'Analyse de votre question…';

/** Each step of the answer being prepared: the current one animated, the previous ones
 *  ticked and written as done (without their « … »). */
const ThinkingIndicator: React.FC<{ steps?: string[]; label?: string }> = ({ steps = [], label = FIRST_STEP }) => {
  if (!steps.length) {
    return (
      <div className="thinking-indicator">
        <Dots />
        <span>{label}</span>
      </div>
    );
  }
  const all = [FIRST_STEP, ...steps];
  return (
    <ul className="thinking-steps" aria-live="polite">
      {all.map((step, i) => {
        const current = i === all.length - 1;
        return (
          <li key={`${i}-${step}`} className={current ? 'thinking-step current' : 'thinking-step'}>
            {current ? <Dots /> : <span className="thinking-check" aria-hidden="true">✓</span>}
            <span>{current ? step : step.replace(/…$/, '')}</span>
          </li>
        );
      })}
    </ul>
  );
};

/* ------------------------------------------------------------------ */
/*  WelcomeScreen                                                     */
/* ------------------------------------------------------------------ */

const SUGGESTION_ICONS = [
  <Tag24Regular key="tag" />,
  <WeatherSnow24Regular key="weather" />,
  <Location24Regular key="lifts" />,
  <ContactCard24Regular key="order" />,
];

/* Fallbacks when GET /api/info is not available yet */
const DEFAULT_SUGGESTIONS = [
  'Quels forfaits journée proposez-vous ?',
  'Quelle météo demain au Pic Blanc ?',
  'Les remontées sont-elles ouvertes ?',
  'Je voudrais réserver un forfait 6 jours',
];
const DEFAULT_SUBTITLE = "Forfaits, pistes, météo, activités, accès… et réservation de votre forfait.";

interface WelcomeScreenProps {
  onSuggestionClick: (text: string) => void;
  chatbotName?: string;
  subtitle?: string;
  suggestions?: string[];
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onSuggestionClick, chatbotName, subtitle, suggestions }) => (
  <div className="welcome-screen">
    <h1 className="welcome-title">{chatbotName ? `Bienvenue à l'${chatbotName}` : 'Comment puis-je vous aider ?'}</h1>
    <p className="welcome-subtitle">
      {chatbotName ? (subtitle || DEFAULT_SUBTITLE) : 'Selectionnez un chatbot pour commencer.'}
    </p>
    <div className="welcome-suggestions">
      {(suggestions?.length ? suggestions : DEFAULT_SUGGESTIONS).map((s, i) => (
        <button key={s} className="suggestion-card" onClick={() => onSuggestionClick(s)}>
          <span className="suggestion-icon">{SUGGESTION_ICONS[i % SUGGESTION_ICONS.length]}</span>
          <span>{s}</span>
        </button>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  ChatInterface (main)                                              */
/* ------------------------------------------------------------------ */

const ChatInterface: React.FC = () => {
  const {
    currentChatbot,
    currentConversation,
    messages,
    documents,
    isStreaming,
    isLoadingMessages,
    chatError,
    pendingInterrupt,
    progress,
    resolveInterrupt,
    sendMessageStream,
    renameConversation,
    createConversation,
    appInfo,
  } = useAppStore();
  const uploadsEnabled = appInfo?.features?.uploads ?? false;
  const voiceEnabled = (appInfo?.features?.voice ?? false) && canRecord();

  const [input, setInput] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [showUploadPopover, setShowUploadPopover] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const recorderRef = useRef<VoiceRecorder | null>(null);

  /* ---- Auto-scroll ---- */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ---- Textarea auto-grow ---- */
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  /* ---- Voice note: record, transcribe, drop the text in the input ---- */
  useEffect(() => {
    if (!isRecording) return;
    const started = Date.now();
    const timer = window.setInterval(() => setRecordSeconds(Math.floor((Date.now() - started) / 1000)), 250);
    return () => window.clearInterval(timer);
  }, [isRecording]);

  // Leaving the conversation mid-recording must not keep the microphone open
  useEffect(() => () => recorderRef.current?.cancel(), []);

  const maxSeconds = appInfo?.voice_max_seconds ?? 120;
  const meter = useCallback(() => recorderRef.current?.level() ?? 0, []);

  const startRecording = async () => {
    setVoiceError(null);
    const recorder = new VoiceRecorder();
    try {
      await recorder.start();
    } catch (e) {
      setVoiceError(e instanceof MicrophoneUnavailableError ? e.message : "Micro indisponible.");
      return;
    }
    recorderRef.current = recorder;
    setRecordSeconds(0);
    setIsRecording(true);
  };

  const stopRecording = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorderRef.current = null; // a second Enter or click must not stop it twice
    setIsRecording(false);
    setIsTranscribing(true);
    try {
      const text = await apiClient.transcribe(await recorder.stop());
      if (text) {
        setInput((current) => (current ? `${current.trimEnd()} ${text}` : text));
        window.setTimeout(adjustTextareaHeight, 0);
        textareaRef.current?.focus();
      } else {
        setVoiceError("Rien n'a été compris. Réessayez en articulant un peu plus.");
      }
    } catch (e: any) {
      setVoiceError(e?.detail || "La transcription a échoué.");
    } finally {
      recorderRef.current = null;
      setIsTranscribing(false);
    }
  };

  const cancelRecording = () => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setIsRecording(false);
  };

  // The server refuses longer notes: stop a second before and transcribe what is there
  const stopRef = useRef(stopRecording);
  stopRef.current = stopRecording;
  const cancelRef = useRef(cancelRecording);
  cancelRef.current = cancelRecording;
  useEffect(() => {
    if (isRecording && recordSeconds >= maxSeconds - 1) void stopRef.current();
  }, [isRecording, recordSeconds, maxSeconds]);

  // Enter validates, Escape drops the note
  useEffect(() => {
    if (!isRecording) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelRef.current();
      else if (e.key === 'Enter') {
        e.preventDefault();
        void stopRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isRecording]);

  const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  const secondsLeft = maxSeconds - recordSeconds;

  /* ---- Close upload popover on outside click ---- */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if click is inside the popover
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      // Don't close if click is inside a Fluent UI Dialog portal
      if (target.closest('[role="dialog"]') || target.closest('.fui-DialogSurface')) return;
      setShowUploadPopover(false);
    };
    if (showUploadPopover) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUploadPopover]);

  /* ---- Focus title input when editing ---- */
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  /* ---- Send logic ---- */
  const handleSend = async () => {
    // The agent answers without documents too (ski pass reservation, small talk)
    if (!input.trim() || isStreaming || !currentChatbot || pendingInterrupt) return;
    const message = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      let convId = currentConversation?.id;
      if (!convId) {
        // Named by the backend after the first exchange (see [CONV] event)
        const conv = await createConversation(currentChatbot.id, 'Nouvelle conversation');
        convId = conv.id;
      }
      await sendMessageStream(convId, message);
    } catch (err) {
      console.error('Chat error:', err);
    } finally {
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* ---- Ensure a conversation exists (documents are scoped per conversation) ---- */
  const ensureConversation = async (): Promise<string | null> => {
    if (currentConversation?.id) return currentConversation.id;
    if (!currentChatbot) return null;
    const conv = await createConversation(currentChatbot.id, 'Nouvelle conversation');
    return conv.id;
  };

  /* ---- Title editing ---- */
  const startEditingTitle = () => {
    if (!currentConversation) return;
    setEditTitle(currentConversation.title);
    setIsEditingTitle(true);
  };

  const saveTitle = async () => {
    if (currentConversation && editTitle.trim()) {
      await renameConversation(currentConversation.id, editTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') saveTitle();
    if (e.key === 'Escape') setIsEditingTitle(false);
  };

  /* ---- Suggestion click ---- */
  const handleSuggestionClick = (text: string) => {
    setInput(text);
    textareaRef.current?.focus();
  };

  /* ---- Derived state ---- */
  const hasDocuments = documents.length > 0;
  const showThinking =
    isStreaming &&
    (messages.length === 0 || messages[messages.length - 1].role !== 'assistant');

  /* ---- Render ---- */
  return (
    <div className="chat-zone">

      {/* ---- Messages area ---- */}
      <div className="chat-messages">
        {isLoadingMessages ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <ThinkingIndicator label="L'assistant réfléchit…" />
          </div>
        ) : !currentChatbot ? (
          <WelcomeScreen onSuggestionClick={handleSuggestionClick} />
        ) : messages.length === 0 && !currentConversation ? (
          <WelcomeScreen
            onSuggestionClick={handleSuggestionClick}
            chatbotName={currentChatbot.name}
            subtitle={appInfo?.description}
            suggestions={appInfo?.suggestions}
          />
        ) : messages.length === 0 ? (
          <WelcomeScreen
            onSuggestionClick={handleSuggestionClick}
            chatbotName={currentChatbot.name}
            subtitle={appInfo?.description}
            suggestions={appInfo?.suggestions}
          />
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageItem
                key={message.id}
                message={message}
                isLatest={index === messages.length - 1}
                isStreaming={isStreaming && index === messages.length - 1}
              />
            ))}

            {showThinking && <ThinkingIndicator steps={progress} />}

            {pendingInterrupt && !isStreaming && (
              <ConfirmationCard
                interrupt={pendingInterrupt}
                onResolve={(responses) => {
                  resolveInterrupt(responses).catch((err) => console.error('Resume error:', err));
                }}
              />
            )}

            <div ref={messagesEndRef} />
          </>
        )}

        {/* Error */}
        {chatError && (
          <div style={{
            background: '#fdf2f2', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '12px 16px', color: '#991b1b', margin: '16px 0',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
          }}>
            <Warning24Regular />
            {chatError}
          </div>
        )}
      </div>

      {/* ---- Input area ---- */}
      <div className="chat-input-area">
        {voiceError && (
          <div className="mic-error" role="status">
            {voiceError}
            <button onClick={() => setVoiceError(null)} aria-label="Fermer"><Dismiss20Regular /></button>
          </div>
        )}
        <div className={`chat-input-wrapper${isRecording || isTranscribing ? ' voice-mode' : ''}`}>
          {isRecording || isTranscribing ? (
            <div className={`voice-bar${isTranscribing ? ' transcribing' : ''}`}>
              <VoiceWaveform level={meter} live={isRecording} />
              <span
                className={`voice-time${isRecording && secondsLeft <= 30 ? ' near-limit' : ''}`}
                title={`${clock(maxSeconds)} au maximum`}
              >
                {isTranscribing ? 'Transcription…' : secondsLeft <= 30 ? `−${clock(Math.max(secondsLeft, 0))}` : clock(recordSeconds)}
              </span>
              <button
                className="voice-btn"
                onClick={cancelRecording}
                disabled={isTranscribing}
                title="Annuler (Échap)"
                aria-label="Annuler l'enregistrement"
              >
                <Dismiss20Regular />
              </button>
              <button
                className="voice-btn voice-confirm"
                onClick={() => void stopRecording()}
                disabled={isTranscribing}
                title="Terminer et transcrire (Entrée)"
                aria-label="Terminer et transcrire"
              >
                {isTranscribing ? <span className="voice-spinner" aria-hidden="true" /> : <Checkmark20Regular />}
              </button>
            </div>
          ) : (
          <>
          <textarea
            ref={textareaRef}
            className="chat-textarea"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              !currentChatbot
                ? 'Selectionnez un chatbot...'
                : pendingInterrupt
                ? 'Repondez a la confirmation ci-dessus...'
                : !hasDocuments
                ? "Posez une question sur l'Alpe d'Huez ou réservez un forfait..."
                : 'Posez votre question...'
            }
            disabled={!currentChatbot || isStreaming || !!pendingInterrupt}
            rows={1}
          />

          <div className="chat-input-actions">
            <div className="left-actions" style={{ position: 'relative' }}>
              {currentChatbot && uploadsEnabled && (
                <>
                  <button
                    onClick={async () => {
                      if (!showUploadPopover) await ensureConversation();
                      setShowUploadPopover(!showUploadPopover);
                    }}
                    title="Joindre un document"
                  >
                    <Attach24Regular />
                  </button>

                  {showUploadPopover && (
                    <div ref={popoverRef} className="upload-popover" style={{ width: 420, maxHeight: 500, overflowY: 'auto' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px 4px', borderBottom: '1px solid #e5e7eb', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1C1C1C' }}>Documents</span>
                        <button
                          onClick={() => setShowUploadPopover(false)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}
                        >
                          <Dismiss20Regular />
                        </button>
                      </div>
                      {currentConversation && (
                        <DocumentManager conversationId={currentConversation.id} />
                      )}
                    </div>
                  )}
                </>
              )}


              {hasDocuments && (
                <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center', marginLeft: 4 }}>
                  {documents.length} doc{documents.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="right-actions">
              {/* Next to send, where ✓ then stands: start and finish without moving */}
              {currentChatbot && voiceEnabled && (
                <button
                  className="mic-button"
                  onClick={startRecording}
                  disabled={isStreaming || !!pendingInterrupt}
                  title="Dicter votre question"
                >
                  <Mic24Regular />
                  <span className="sr-only">Dicter</span>
                </button>
              )}
            <button
              className="send-button"
              onClick={handleSend}
              disabled={!input.trim() || isStreaming || !currentChatbot || !!pendingInterrupt}
            >
              <Send24Regular />
              <span className="sr-only">Envoyer</span>
            </button>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
